import moment from "moment";
import { groupOrdersForEditing } from "../../../Account/Subscriptions/ToggleableOrdersList/ToggleableOrdersList-utils";
import { LogLevel } from "../../../Shared/hooks/useLogging/Logging-types";
export var SubscriptionSummaryText;
(function (SubscriptionSummaryText) {
    SubscriptionSummaryText["CHANGES_TAKE_EFFECT"] = "Your changes will take effect on {placeHolder}";
    SubscriptionSummaryText["YOUR_NEXT_ORDER"] = "your next order";
    SubscriptionSummaryText["UPDATE_SUCCESS"] = "Updated Successfully";
    SubscriptionSummaryText["GENERIC_ERROR"] = "Something unexpected happened. Please start over or contact support.";
    SubscriptionSummaryText["UNCATEGORIZED"] = "Uncategorized";
    SubscriptionSummaryText["DEFAULT_HEADER"] = "Subscription Summary";
    SubscriptionSummaryText["UPDATE_ORDERS"] = "Update Orders";
    SubscriptionSummaryText["LOADING"] = "Loading...";
    SubscriptionSummaryText["SAVING"] = "Your changes are being saved...";
    SubscriptionSummaryText["PAYMENT_SUCCESS"] = "Payment successful! Your order has been updated.";
})(SubscriptionSummaryText || (SubscriptionSummaryText = {}));
/**
 * If any of the following cart selections have changed the post Edit Flow ToggleableOrdersList will display.
 *
 * - Days Of Week
 * - Meal Bundle Chooser
 * - MealQuantity
 * - Meal Size
 * - Products
 *
 * @param oldSelections
 * @param newSelections
 * @returns
 */
export const compareSelections = (oldSelections, newSelections) => {
    var _a, _b;
    let differentSelections = false;
    if (!oldSelections) {
        return differentSelections;
    }
    if (oldSelections.daysOfWeek.length !== newSelections.daysOfWeek.length ||
        !oldSelections.daysOfWeek.every((day, index) => day === newSelections.daysOfWeek[index])) {
        differentSelections = true;
    }
    if (oldSelections.mealQuantity.length !== newSelections.mealQuantity.length ||
        !oldSelections.mealQuantity.every((mq, index) => mq.programMealId === newSelections.mealQuantity[index].programMealId &&
            mq.quantity === newSelections.mealQuantity[index].quantity &&
            moment(mq.mealDate).isSame(newSelections.mealQuantity[index].mealDate))) {
        differentSelections = true;
    }
    if (oldSelections.mealBundles.length !== newSelections.mealBundles.length ||
        !oldSelections.mealBundles.every((bundle, bundleIndex) => bundle.meals.every((bm, mealIndex) => bm.programMealId === newSelections.mealBundles[bundleIndex].meals[mealIndex].programMealId &&
            bm.quantity === newSelections.mealBundles[bundleIndex].meals[mealIndex].quantity &&
            bm.dayOfWeek === newSelections.mealBundles[bundleIndex].meals[mealIndex].dayOfWeek))) {
        differentSelections = true;
    }
    if (((_a = oldSelections.mealSize) === null || _a === void 0 ? void 0 : _a.Size) !== ((_b = newSelections.mealSize) === null || _b === void 0 ? void 0 : _b.Size)) {
        differentSelections = true;
    }
    if (oldSelections.products.length !== newSelections.products.length ||
        !oldSelections.products.every((p, index) => p.id === newSelections.products[index].id &&
            p.quantity === newSelections.products[index].quantity &&
            p.dayOfWeek === newSelections.products[index].dayOfWeek)) {
        differentSelections = true;
    }
    return differentSelections;
};
/**
 * Returns the current selections from the cart for the following modules:
 *
 * - Days Of Week
 * - Meal Bundle Chooser
 * - MealQuantity
 * - Meal Size
 * - Products
 *
 * @param {Cart} cart
 * @returns
 */
export const getCurrentSelections = (cart) => {
    return {
        daysOfWeek: cart.daysOfWeek,
        mealSize: cart.mealSize,
        mealQuantity: cart.meals.map((meal) => {
            return {
                programMealId: meal.ProgramMealId,
                mealDate: meal.MealDate,
                quantity: meal.Quantity,
            };
        }),
        mealBundles: cart.bundles.flatMap((bundle) => {
            return {
                name: bundle.BundleName,
                quantity: bundle.Quantity,
                meals: bundle.BundleMeals.map((bundleMeal) => {
                    return {
                        programMealId: bundleMeal.ProgramMealId,
                        quantity: bundleMeal.Quantity,
                        dayOfWeek: bundleMeal.DayOfWeek,
                    };
                }),
            };
        }),
        products: cart.products
            .filter((p) => p.quantity > 0)
            .map((product) => {
            return {
                id: product.productId,
                quantity: product.quantity,
                dayOfWeek: product.DayOfWeek,
            };
        }),
    };
};
/**
 * This function is called when the subscription orders fetch fails.
 * @param setSubscriptionOrdersState
 * @param toast
 * @param datadogLog
 * @param error
 * @param closeEditModal
 */
export const onGetSubOrdersFailure = (setSubscriptionOrdersState, toast, datadogLog, error, closeEditModal) => {
    toast.push(SubscriptionSummaryText.GENERIC_ERROR, { variant: "error" });
    datadogLog("Failed to fetch orders for subscription in SubscriptionSummaryPage", LogLevel.ERROR, undefined, error);
    closeEditModal && closeEditModal();
    // Since we're not fetching orders successfully, set loading to false here as well
    setSubscriptionOrdersState((prev) => (Object.assign(Object.assign({}, prev), { loading: false })));
};
/**
 * This function is called when the subscription orders are successfully fetched.
 * @param setSubscriptionOrdersState
 * @param store
 * @param res
 */
export const onGetSubOrdersSuccess = (setSubscriptionOrdersState, store, res) => {
    const { orderGroups, selectedOrders } = groupOrdersForEditing(res.data.orders, store);
    setSubscriptionOrdersState((prev) => (Object.assign(Object.assign({}, prev), { orders: res.data.orders, loading: false, orderGroups: orderGroups, selectedOrders: selectedOrders })));
};
/**
 * Checks if the "Active and Future Orders" group has any orders in it.
 * @param orderGroups
 * @returns boolean
 */
export const checkActiveAndFutureOrdersLength = (orderGroups) => {
    // Find the group with the title "Active and Future Orders"
    const activeAndFutureOrdersGroup = orderGroups.find((group) => group.title === "Active and Future Orders");
    // Check if the group exists and if its orders array has a length greater than 0
    if (activeAndFutureOrdersGroup && activeAndFutureOrdersGroup.orders.length > 0) {
        return true; // The orders array has a length
    }
    return false; // The orders array is empty or the group does not exist
};
/**
 * This function is called when the user clicks the "Continue" button on the SubscriptionSummaryPage.
 * @param datadogLog
 * @param isSaving
 * @param params
 * @param SaveOrderFlowSession
 * @param setIsUpdatingPrice
 * @param setSaveState
 * @param store
 * @param subscriptionOrdersState
 * @param toast
 */
export const updateSubscriptionData = (datadogLog, isSaving, params, SaveOrderFlowSession, setIsUpdatingPrice, setSaveState, setBalance, setOrdersWithBalance, setSubscriptionOrdersState, store, subscriptionOrdersState, toast) => {
    const windowTop = window.top;
    if (!isSaving) {
        setSaveState((prev) => (Object.assign(Object.assign({}, prev), { isSaving: true })));
        setIsUpdatingPrice(true);
        SaveOrderFlowSession(store.cart, store.model, true, false, false, undefined, 
        // selectedOrders is passed only when displaying the upcoming orders list.
        // Otherwise, only the template is updated, and no orders will be changed.
        subscriptionOrdersState.showUpcomingOrdersList
            ? subscriptionOrdersState.selectedOrders.map((o) => {
                return { orderId: o.OrderId, orderStatus: o.OrderStatus };
            })
            : [])
            .then((res) => {
            var _a;
            toast.push(SubscriptionSummaryText.UPDATE_SUCCESS, { variant: "success" });
            if (((_a = res.data.ordersWithBalance) === null || _a === void 0 ? void 0 : _a.length) > 0) {
                setBalance(res.data.ordersWithBalance[0].UpdatedBalance);
                setOrdersWithBalance({
                    orderInfo: res.data.orderInfo,
                    orders: res.data.ordersWithBalance.map((o) => {
                        const order = subscriptionOrdersState.selectedOrders.find((so) => so.OrderId === o.UpdatedBalance.orderId);
                        return Object.assign(Object.assign({}, order), { balance: o.UpdatedBalance, completed: false });
                    }),
                });
                setIsUpdatingPrice(false);
                setSaveState((prev) => (Object.assign(Object.assign({}, prev), { isSaved: false, isSaving: false })));
                setSubscriptionOrdersState((prev) => (Object.assign(Object.assign({}, prev), { showUpcomingOrdersList: false, showPaymentForm: true })));
            }
            else {
                setIsUpdatingPrice(false);
                setSaveState((prev) => (Object.assign(Object.assign({}, prev), { isSaved: true })));
                /**
                 * Redirect after successful update.
                 * @params successUrl is a query string param that is only present when opened from the mobile app.
                 * We use the presence of this param to determine the redirect url.
                 */
                windowTop.location.href = !!params.successUrl ? params.successUrl : "/Account/Subscriptions";
            }
        })
            .catch((err) => {
            datadogLog("SaveOrderFlowSession failed on SubscriptionSummaryPage", LogLevel.ERROR, undefined, err);
            toast.push(SubscriptionSummaryText.GENERIC_ERROR, { variant: "error" });
            setSaveState((prev) => (Object.assign(Object.assign({}, prev), { isSaving: false, isSaved: false })));
            setIsUpdatingPrice(false);
            /**
             * Redirect after unsuccessful update.
             * @params failureUrl is a query string param that is only present when opened from the mobile app.
             * We use the presence of this param to determine the redirect url.
             * The existing logic does not redirect upon failure when in the web-app; only redirect if opened from mobile.
             */
            if (!!params.failureUrl)
                windowTop.location.href = params.failureUrl;
        });
    }
};
export const onSuccess = (toast, balance, ordersWithBalance, setSaveState, setOrdersWithBalance, params, setBalance) => (res) => {
    if (!res.data.success) {
        toast.push(SubscriptionSummaryText.GENERIC_ERROR, { variant: "error" });
        setSaveState((prev) => (Object.assign(Object.assign({}, prev), { isSaving: false })));
        return;
    }
    toast.push(SubscriptionSummaryText.PAYMENT_SUCCESS, { variant: "success" });
    // Set the current order as complete
    const updatedOrders = ordersWithBalance === null || ordersWithBalance === void 0 ? void 0 : ordersWithBalance.orders.map((order) => order.balance.orderId === balance.orderId ? Object.assign(Object.assign({}, order), { completed: true }) : order);
    setOrdersWithBalance((prev) => (Object.assign(Object.assign({}, prev), { orders: updatedOrders })));
    const nextOrderToBePaid = updatedOrders === null || updatedOrders === void 0 ? void 0 : updatedOrders.find((order) => order.balance.orderId !== balance.orderId && !order.completed);
    if (nextOrderToBePaid) {
        setBalance(nextOrderToBePaid.balance);
    }
    setSaveState((prev) => (Object.assign(Object.assign({}, prev), { isSaving: false })));
    // Add order have been paid and updated to refresh the page
    if (!nextOrderToBePaid) {
        /**
         * Redirect after successful update.
         * @params successUrl is a query string param that is only present when opened from the mobile app.
         * We use the presence of this param to determine the redirect url.
         */
        window.top.location.href = !!params.successUrl ? params.successUrl : "/Account/Subscriptions";
    }
};
export const onError = (toast, setSaveState) => (err) => {
    var _a;
    const message = (_a = err.message) !== null && _a !== void 0 ? _a : SubscriptionSummaryText.GENERIC_ERROR;
    toast.push(message, { variant: "error" });
    setSaveState((prev) => (Object.assign(Object.assign({}, prev), { isSaving: false })));
};
